'use client'

import { useCallback, useEffect, useState } from 'react'

import { clearDeferredPrompt, getDeferredPrompt, subscribeDeferredPrompt } from '../pwa-install-capture'

const DEFAULT_DISMISSED_KEY = 'ordo-install-prompt-dismissed'

interface NavigatorWithStandalone extends Navigator {
  standalone?: boolean
}

function detectInstalled(): boolean {
  // Next.js SSR build evaluates this module on Node, which has
  // globalThis but no matchMedia. Guard against the missing API.
  if (typeof globalThis === 'undefined' || typeof globalThis.matchMedia !== 'function') {
    return false
  }
  if (globalThis.matchMedia('(display-mode: standalone)').matches) {
    return true
  }
  if (typeof navigator === 'undefined') {
    return false
  }
  return (navigator as NavigatorWithStandalone).standalone === true
}

function detectIosSafari(): boolean {
  if (typeof navigator === 'undefined') {
    return false
  }
  const ua = navigator.userAgent
  // MSStream excludes IE11 on Windows Phone, which spoofed iOS UA strings.
  const msStream = (globalThis as unknown as { MSStream?: unknown }).MSStream
  return /iPhone|iPad|iPod/.test(ua) && !msStream
}

export interface UsePwaInstallOptions {
  /**
   * localStorage key used to persist the user's "dismiss" choice.
   * Pass an app-scoped key when running multiple Ordo apps on the same
   * origin in dev (e.g. via ngrok rotation) so dismissals don't bleed.
   * Defaults to a shared key — fine in production where each app has
   * its own subdomain.
   */
  dismissedKey?: string
}

export interface UsePwaInstallResult {
  /** True when Chrome's prompt is captured and the user hasn't dismissed/installed. */
  installable: boolean
  /** True on iOS Safari (which never fires `beforeinstallprompt`) when not yet installed. */
  iosA2HS: boolean
  /** True after `appinstalled` fires or a standalone display-mode is detected. */
  installed: boolean
  /** True after the user clicked "Dismiss" on the toast. */
  dismissed: boolean
  /** Triggers Chrome's native install prompt; returns the user's choice. */
  triggerInstall: () => Promise<{ outcome: 'accepted' | 'dismissed' | 'unavailable' }>
  /** Persist a dismissal so the toast doesn't reappear on every reload. */
  dismiss: () => void
}

export function usePwaInstall(options: UsePwaInstallOptions = {}): UsePwaInstallResult {
  const dismissedKey = options.dismissedKey ?? DEFAULT_DISMISSED_KEY
  const [hasPrompt, setHasPrompt] = useState(() => getDeferredPrompt() !== null)
  const [installed, setInstalled] = useState(() => detectInstalled())
  const [iosA2HS] = useState(() => detectIosSafari() && !detectInstalled())
  const [dismissed, setDismissed] = useState(() => {
    if (typeof localStorage === 'undefined') {
      return false
    }
    return localStorage.getItem(dismissedKey) === '1'
  })

  useEffect(() => {
    const unsubscribe = subscribeDeferredPrompt((event) => {
      setHasPrompt(event !== null)
    })
    const handleInstalled = () => {
      setInstalled(true)
    }
    globalThis.addEventListener('appinstalled', handleInstalled)
    return () => {
      unsubscribe()
      globalThis.removeEventListener('appinstalled', handleInstalled)
    }
  }, [])

  const triggerInstall = useCallback(async () => {
    const event = getDeferredPrompt()
    if (!event) {
      return { outcome: 'unavailable' as const }
    }
    await event.prompt()
    const { outcome } = await event.userChoice
    clearDeferredPrompt()
    if (outcome === 'accepted') {
      setInstalled(true)
    }
    return { outcome }
  }, [])

  const dismiss = useCallback(() => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(dismissedKey, '1')
    }
    setDismissed(true)
  }, [dismissedKey])

  return {
    installable: hasPrompt && !installed && !dismissed,
    iosA2HS: iosA2HS && !dismissed,
    installed,
    dismissed,
    triggerInstall,
    dismiss,
  }
}
